import { useState } from "react";
import {
	Box,
	Button,
	Container,
	Flex,
	Heading,
	Image,
	Link,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Text,
	useDisclosure,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FiMaximize2 } from "react-icons/fi";

export type Mark = {
	label: string;
	text: string;
	box: [number, number, number, number];
};
export type GuideImage = {
	file: string;
	alt: string;
	width: number;
	height: number;
	crop?: [number, number];
	marks: Mark[];
};
export type Task = {
	title: string;
	steps: string[];
	result: string;
	tip?: string;
	image?: GuideImage;
};
export type GuideContent = {
	title: string;
	intro: string;
	access?: string;
	tasks: Task[];
	related: [string, string][];
};

export function AnnotatedImage({ image }: { image: GuideImage }) {
	const [active, setActive] = useState(0);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const picture = (large = false) => (
		<Box
			position="relative"
			w={large ? `${image.width}px` : "full"}
			maxW={large ? "none" : `${image.width}px`}
			mx="auto"
			overflow="hidden"
			style={{ aspectRatio: `${image.width} / ${image.height}` }}
		>
			<Image
				src={`/screenshots/guide/${image.file}.jpg`}
				alt={image.alt}
				position="absolute"
				left={`${(-(image.crop?.[0] ?? 0) / image.width) * 100}%`}
				top={`${(-(image.crop?.[1] ?? 0) / image.height) * 100}%`}
				w={`${(1280 / image.width) * 100}%`}
				maxW="none"
				loading="lazy"
			/>
			{image.marks.map((mark, i) => (
				<Box
					key={mark.label}
					aria-hidden="true"
					position="absolute"
					pointerEvents="none"
					left={`${(mark.box[0] / image.width) * 100}%`}
					top={`${(mark.box[1] / image.height) * 100}%`}
					width={`${(mark.box[2] / image.width) * 100}%`}
					height={`${(mark.box[3] / image.height) * 100}%`}
					border={i === active ? "3px solid" : "2px solid"}
					borderColor={i === active ? "orange.600" : "blue.600"}
					borderRadius="4px"
					bg={i === active ? "orange.100" : "transparent"}
					sx={{
						backgroundColor:
							i === active ? "rgba(255, 166, 0, .09)" : "transparent",
					}}
				>
					<Flex
						position="absolute"
						top="-13px"
						left="-10px"
						w="26px"
						h="26px"
						align="center"
						justify="center"
						borderRadius="full"
						bg={i === active ? "orange.700" : "blue.700"}
						color="white"
						fontSize="sm"
						fontWeight="bold"
						border="2px solid white"
					>
						{i + 1}
					</Flex>
				</Box>
			))}
		</Box>
	);
	return (
		<Box
			as="figure"
			m={0}
			mt={5}
			border="1px solid"
			borderColor="gray.200"
			borderRadius="xl"
			overflow="hidden"
			bg="white"
		>
			<Box p={{ base: 3, md: 5 }}>{picture()}</Box>
			<Box
				as="figcaption"
				p={4}
				borderTop="1px solid"
				borderColor="gray.100"
				bg="gray.50"
			>
				<Flex align="center" justify="space-between" gap={3} wrap="wrap" mb={3}>
					<Text fontSize="sm" color="gray.600">
						Учебный пример. Выберите пояснение — нужное место выделится.
					</Text>
					<Button
						size="sm"
						variant="outline"
						leftIcon={<FiMaximize2 />}
						onClick={onOpen}
					>
						Увеличить снимок
					</Button>
				</Flex>
				<Flex gap={2} wrap="wrap" mb={2}>
					{image.marks.map((mark, i) => (
						<Button
							key={mark.label}
							size="sm"
							h="auto"
							minH="44px"
							py={2}
							whiteSpace="normal"
							textAlign="left"
							variant={i === active ? "solid" : "outline"}
							colorScheme={i === active ? "orange" : "gray"}
							onClick={() => setActive(i)}
							aria-pressed={i === active}
						>
							{i + 1}. {mark.label}
						</Button>
					))}
				</Flex>
				<Text fontSize="sm" color="gray.700" aria-live="polite">
					{image.marks[active]?.text}
				</Text>
			</Box>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				size="6xl"
				scrollBehavior="inside"
			>
				<ModalOverlay />
				<ModalContent mx={3} maxH="90vh">
					<ModalHeader pr={12} fontSize="lg">
						{image.alt}
					</ModalHeader>
					<ModalCloseButton aria-label="Закрыть снимок" />
					<ModalBody pb={5} overflow="auto">
						<Text mb={4} fontSize="sm">
							Если снимок не помещается, прокрутите его вбок. Цифры
							соответствуют пояснениям под снимком.
						</Text>
						{picture(true)}
					</ModalBody>
				</ModalContent>
			</Modal>
		</Box>
	);
}

// Bold only actual labels and short key phrases, marked with ** in editorial copy.
export function Copy({ text }: { text: string }) {
	return (
		<>
			{text
				.split(/(\*\*.*?\*\*)/g)
				.map((part, i) =>
					part.startsWith("**") ? (
						<strong key={i}>{part.slice(2, -2)}</strong>
					) : (
						part
					),
				)}
		</>
	);
}

export function Guide({ content }: { content: GuideContent }) {
	return (
		<Container maxW="1100px" px={0} py={{ base: 4, md: 6 }}>
			<Text color="brand.700" fontWeight="bold" fontSize="sm" mb={2}>
				СПРАВКА ПО ШАГАМ
			</Text>
			<Heading as="h1" size={{ base: "xl", md: "2xl" }} mb={4}>
				{content.title}
			</Heading>
			<Text fontSize="lg" color="gray.600" maxW="760px" mb={4}>
				{content.intro}
			</Text>
			{content.access && (
				<Box borderLeft="3px solid" borderColor="brand.500" pl={4} mb={6}>
					<Text fontSize="sm" color="gray.600">
						{content.access}
					</Text>
				</Box>
			)}
			<Text fontSize="sm" mb={4} color="gray.600">
				Откройте нужный вопрос. Остальные можно пропустить.
			</Text>
			{content.tasks.map((task, i) => (
				<Box
					as="details"
					key={task.title}
					open={i === 0 ? true : undefined}
					border="1px solid"
					borderColor="gray.200"
					borderRadius="xl"
					bg="white"
					mb={4}
					overflow="hidden"
				>
					<Box
						as="summary"
						cursor="pointer"
						p={{ base: 4, md: 5 }}
						fontWeight="bold"
						fontSize={{ base: "md", md: "lg" }}
						_hover={{ bg: "gray.50" }}
						_focusVisible={{
							outline: "3px solid",
							outlineColor: "brand.500",
							outlineOffset: "-3px",
						}}
					>
						{task.title}
					</Box>
					<Box px={{ base: 4, md: 6 }} pb={6}>
						<Box
							as="ol"
							pl={6}
							sx={{
								"& li": {
									paddingLeft: "6px",
									marginBottom: "12px",
									lineHeight: 1.75,
								},
								"& li::marker": { fontWeight: 700, color: "#00766c" },
							}}
						>
							{task.steps.map((step) => (
								<li key={step}>
									<Copy text={step} />
								</li>
							))}
						</Box>
						<Box bg="brand.50" borderRadius="lg" px={4} py={3} mt={4}>
							<Text>
								<strong>Готово: </strong>
								<Copy text={task.result} />
							</Text>
						</Box>
						{task.tip && (
							<Text mt={4} fontSize="sm" color="gray.600">
								<Copy text={task.tip} />
							</Text>
						)}
						{task.image && <AnnotatedImage image={task.image} />}
					</Box>
				</Box>
			))}
			<Flex gap={3} wrap="wrap" mt={7}>
				{content.related.map(([path, label]) => (
					<Link
						key={path}
						as={RouterLink}
						to={path}
						color="brand.700"
						fontWeight="semibold"
						textDecoration="underline"
						py={2}
					>
						{label} →
					</Link>
				))}
			</Flex>
		</Container>
	);
}
